// ============================================================================
// JARVISTRAVEL - DEMO STAYS STORE (JAR-1995)
// The demo serves a trip's stays from the browser, never core. This holds the
// stays a visitor has changed in the demo (added or edited), keyed by SAMPLE
// TRIP id, so they survive a reload and a trip switch for the session. Until a
// visitor changes anything, a trip shows its own SAMPLE_STAYS_BY_TRIP; the first
// change seeds from those samples so the trip's own stays are kept beside it.
// Live mode never touches this. Cleared with the rest of the demo's data when a
// demo starts and ends (demoDataPurge, JAR-1992).
// ============================================================================

import type { Property } from '@/app/types';
import { SAMPLE_STAYS_BY_TRIP } from './sampleData';

const KEY_PREFIX = 'jarvis_demo_stays_v1:';
const key = (tripId: string) => `${KEY_PREFIX}${tripId}`;

/** Shape check - downstream reads `id` and renders `name`. */
function isStoredStay(v: unknown): v is Property {
  if (!v || typeof v !== 'object') return false;
  const p = v as { id?: unknown; name?: unknown };
  return typeof p.id === 'string' && typeof p.name === 'string';
}

/** The stays stored for a demo trip, or null when none are (the caller then
 *  falls back to the trip's sample stays). Corrupt entries are dropped. */
function readStored(tripId: string): Property[] | null {
  try {
    const raw = localStorage.getItem(key(tripId));
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter(isStoredStay) : null;
  } catch {
    return null;
  }
}

function write(tripId: string, stays: Property[]): void {
  try {
    localStorage.setItem(key(tripId), JSON.stringify(stays));
  } catch {
    /* quota/unavailable - demo persistence is best-effort */
  }
}

/** The demo's stays for a trip: the ones the visitor has changed this session,
 *  or the trip's own sample stays when they have changed none. The single list
 *  the demo Stays tab reads (JAR-1995). */
export function demoStaysFor(tripId: string): Property[] {
  return readStored(tripId) ?? SAMPLE_STAYS_BY_TRIP[tripId] ?? [];
}

/** Append a stay the visitor added. Seeds from the sample stays first, so the
 *  trip's own stays are kept beside the new one. Returns the new list. */
export function addDemoStay(tripId: string, stay: Property): Property[] {
  const next = [...demoStaysFor(tripId), stay];
  write(tripId, next);
  return next;
}

/** Replace a stay the visitor edited, matched by id. Returns the new list. */
export function updateDemoStay(tripId: string, stay: Property): Property[] {
  const next = demoStaysFor(tripId).map((s) => (s.id === stay.id ? stay : s));
  write(tripId, next);
  return next;
}

/** Forget a trip's demo stays, so it shows its sample stays again. Best-effort -
 *  clears demo data when a demo starts and ends (JAR-1992). */
export function clearDemoStays(tripId: string): void {
  try {
    localStorage.removeItem(key(tripId));
  } catch {
    /* storage unavailable - nothing to clear */
  }
}
